/**
 * AI denoise bridge for /auto-captions — upgrade path from FFmpeg's afftdn
 * (cheap, frequency-domain noise gate) to DeepFilterNet3 (ONNX model used
 * by /denoise, /audio-enhancer, /edit-video-sound).
 *
 * The pipeline runs Whisper on Float32 PCM at 16 kHz mono. DeepFilter
 * operates on AudioBuffer @ 48 kHz. This module hides the resampling
 * round-trip so the pipeline call site is one line:
 *
 *   if (window.__acAIDenoise && window.__acAIDenoise.isEnabled() && window.__acAIDenoise.getMode() === 'ai') {
 *       try { f32 = await window.__acAIDenoise.process(f32, 16000, onProg); }
 *       catch (e) { ... fall back to afftdn ... }
 *   }
 *
 * UI: the existing single "Denoise" checkbox in the blade is paired with a
 * mode <select> ("Fast" = afftdn / "AI" = DeepFilter). When DeepFilter is
 * not available on the device (mobile RAM gate, blocked CDN, init failure)
 * the select is forced to "Fast" and disabled so the user never sees the
 * AI option silently no-op.
 *
 * Public surface (window.__acAIDenoise):
 *   isEnabled()              → boolean — checkbox is on
 *   getMode()                → 'fast' | 'ai' — dropdown value, capped to
 *                              'fast' when isAvailable() is false
 *   isAvailable()            → boolean — DeepFilterProcessor present
 *   process(pcmF32, srcRate, onProgress) → Promise<Float32Array>
 *                              same shape & length as input (16 kHz mono)
 *
 * No state is stored on the module — the checkbox/select live in the DOM
 * and are read fresh on every isEnabled/getMode call so the pipeline never
 * desyncs from the UI.
 */
(function(){
    'use strict';

    var CHECKBOX_ID = 'ac-preproc-denoise';
    var MODE_SELECT_ID = 'ac-preproc-denoise-mode';
    var TARGET_RATE = 48000; // DeepFilterNet3 native sample rate

    function $(id){ return document.getElementById(id); }

    // DeepFilterNet runs in a module worker. On an engine without them (Firefox <
    // 114, Safari < 15) the AI mode used to be chosen anyway: the extraction then
    // skipped the fast filter it replaces, the AI step failed, and the words were
    // read from audio with no denoise at all. Not available there means getMode()
    // answers 'fast' before extraction, and the fast filter is applied.
    function isAvailable(){
        if (window.__moduleWorkerOk && !window.__moduleWorkerOk()) return false;
        return typeof window.DeepFilterProcessor !== 'undefined' &&
               typeof window.DeepFilterProcessor.init === 'function' &&
               typeof window.DeepFilterProcessor.process === 'function';
    }

    function isEnabled(){
        var cb = $(CHECKBOX_ID);
        return !!(cb && cb.checked);
    }

    function getMode(){
        var sel = $(MODE_SELECT_ID);
        var raw = (sel && sel.value) ? String(sel.value).toLowerCase() : 'fast';
        if (raw !== 'ai' && raw !== 'fast') raw = 'fast';
        // Force fast when the AI engine isn't on this device — guards against
        // a stale localStorage value or a user who toggled before scripts loaded.
        if (raw === 'ai' && !isAvailable()) return 'fast';
        return raw;
    }

    /**
     * Resample a Float32 mono PCM buffer between two sample rates using
     * OfflineAudioContext. Returns a fresh Float32Array.
     */
    async function resampleMono(pcmF32, fromRate, toRate){
        if (fromRate === toRate) {
            var copy = new Float32Array(pcmF32.length);
            copy.set(pcmF32);
            return copy;
        }
        // Source buffer @ fromRate with 1 channel
        // Note: AudioContext for buffer creation can use any sample rate.
        var srcCtx = new OfflineAudioContext(1, Math.max(1, pcmF32.length), fromRate);
        var srcBuf = srcCtx.createBuffer(1, pcmF32.length, fromRate);
        srcBuf.getChannelData(0).set(pcmF32);

        var outLen = Math.max(1, Math.ceil(pcmF32.length * toRate / fromRate));
        var dstCtx = new OfflineAudioContext(1, outLen, toRate);
        var node = dstCtx.createBufferSource();
        node.buffer = srcBuf;
        node.connect(dstCtx.destination);
        node.start();
        var rendered = await dstCtx.startRendering();
        var out = new Float32Array(rendered.length);
        out.set(rendered.getChannelData(0));
        return out;
    }

    /**
     * Wrap a Float32Array (mono @ targetRate) as an AudioBuffer so
     * DeepFilterProcessor.process can consume it.
     */
    function wrapAsBuffer(f32, rate){
        var ctx = new OfflineAudioContext(1, f32.length, rate);
        var buf = ctx.createBuffer(1, f32.length, rate);
        buf.getChannelData(0).set(f32);
        return buf;
    }

    /**
     * Main entry: take 16 kHz mono Float32 PCM, return denoised 16 kHz mono
     * Float32 PCM (same length, ±1 sample due to resample rounding — we
     * clamp/pad to keep length stable so VAD offset math and silence-guard
     * indices line up exactly with the original buffer).
     *
     * On any internal failure, throws — the pipeline catch block falls back
     * to afftdn so the user still gets denoise, just the cheaper kind.
     */
    async function process(pcmF32, sourceRate, onProgress){
        if (!isAvailable()) throw new Error('DeepFilterProcessor unavailable');
        if (!(pcmF32 instanceof Float32Array) || !pcmF32.length) {
            throw new Error('AI denoise: empty PCM');
        }
        sourceRate = sourceRate || 16000;
        var origLen = pcmF32.length;
        var prog = typeof onProgress === 'function' ? onProgress : function(){};

        // Init the worker (idempotent — second call to init returns immediately
        // when already loaded). We treat init progress as the first 5 % of the
        // overall AI-denoise bar so the user sees movement during cold start.
        prog(0.01);
        var initOk = await window.DeepFilterProcessor.init(function(r){
            prog(0.01 + (r || 0) * 0.04);
        });
        if (!initOk) {
            throw window.DeepFilterProcessor.lastError || new Error('DeepFilter init failed');
        }
        prog(0.05);

        // Upsample 16 k → 48 k. Fast on hardware; no audible quality loss
        // since we'll downsample back at the end.
        var up = await resampleMono(pcmF32, sourceRate, TARGET_RATE);
        prog(0.1);

        // Feed to DeepFilter as AudioBuffer @ 48 kHz. strength=1.0 → full wet.
        // The processor's gain-transfer math is mono-safe (channels === 1
        // path mixes wet/dry with the supplied strength).
        var buf48 = wrapAsBuffer(up, TARGET_RATE);
        var dfResult = await window.DeepFilterProcessor.process(buf48, 1.0, function(r){
            // Map DeepFilter's 0..1 into our 0.1 .. 0.9 band.
            prog(0.1 + (r || 0) * 0.8);
        });
        if (!dfResult || !dfResult.channels || !dfResult.channels[0]) {
            throw new Error('DeepFilter returned empty result');
        }
        var processed48 = dfResult.channels[0];
        prog(0.9);

        // Downsample 48 k → source rate (16 k typically). Pin output length
        // to origLen so downstream indexing (VAD windows, silence guard) is
        // unchanged. OfflineAudioContext rounding can produce ±1 sample.
        var processedSrc = await resampleMono(processed48, TARGET_RATE, sourceRate);
        var out;
        if (processedSrc.length === origLen) {
            out = processedSrc;
        } else if (processedSrc.length > origLen) {
            out = processedSrc.subarray(0, origLen);
            // subarray shares the buffer — copy so the caller can transfer.
            var copy = new Float32Array(origLen);
            copy.set(out);
            out = copy;
        } else {
            out = new Float32Array(origLen);
            out.set(processedSrc, 0);
            // Trailing samples (≤ 1-2) stay zero — inaudible.
        }
        prog(1);
        return out;
    }

    window.__acAIDenoise = {
        isEnabled: isEnabled,
        getMode: getMode,
        isAvailable: isAvailable,
        process: process,
    };
})();
